/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import type { ComponentProps } from 'react';

import type { Size, Parameters, RecaptchaCheckboxFallbackProps } from './recaptcha.types';
import { removeRecaptchaContainer } from './utils';
import { useRecaptcha } from './RecaptchaContext';

import styles from './RecaptchaCheckbox.module.scss';

export interface RecaptchaCheckboxProps
    extends
        Omit<ComponentProps<'div'>, 'onChange'>,
        Partial<RecaptchaCheckboxFallbackProps> {
    /** Action identifier for analytics */
    action?: string;
    /** Size of the checkbox widget */
    size?: Size['v2-checkbox'];
    /**
     * When true, the checkbox only renders when isChallengeRequired is true.
     * When false (default), the checkbox always renders (standalone v2 mode).
     * Requires isChallengeRequired prop to be provided when true.
     */
    isFallbackOnly?: boolean;
    /** Callback when verification is successful */
    onChange?: (token: string) => void;
    /** Callback when reCAPTCHA encounters an error */
    onError?: () => void;
    /** Callback when the reCAPTCHA response expires */
    onExpired?: () => void;
}

const CHECKBOX_CONTAINER_ID = 'recaptcha-checkbox-container';

export const recaptchaV2ValidationError = {
    inputId: CHECKBOX_CONTAINER_ID,
    errorText: 'Complete the reCAPTCHA challenge.',
} as const;

/**
 * RecaptchaCheckbox
 *
 * A v2 checkbox component that can operate in two modes:
 * - Fallback mode: Only renders when isChallengeRequired is true (use with useRecaptchaWithFallback)
 * - Standalone mode: Always renders as a v2 checkbox
 *
 * When used with useRecaptchaWithFallback, spread the recaptchaCheckboxProps:
 *
 * @example
 * // Fallback mode with useRecaptchaWithFallback
 * const { recaptchaCheckboxProps } = useRecaptchaWithFallback();
 * <RecaptchaCheckbox {...recaptchaCheckboxProps} isFallbackOnly action="login" />
 *
 * @example
 * // Standalone mode - always renders as a v2 checkbox
 * <RecaptchaCheckbox onChange={(token) => setToken(token)} />
 */
export function RecaptchaCheckbox({
    id = CHECKBOX_CONTAINER_ID,
    action,
    size = 'normal',
    isFallbackOnly = false,
    isChallengeRequired = false,
    onTokenChange,
    onChange,
    onError,
    onExpired,
    className,
    ...props
}: RecaptchaCheckboxProps) {
    // Get shared resources from the provider (instance and v2SiteKey)
    const { instance, v2SiteKey } = useRecaptcha();

    // Determine if the checkbox should be visible
    // In fallback mode: only show when challenge is required
    // In standalone mode: always show
    const shouldRender = isFallbackOnly ? isChallengeRequired : true;
    const checkboxContainerRef = React.useRef<HTMLDivElement>(null);
    const widgetIdRef = React.useRef<number | null>(null);
    const hasRenderedRef = React.useRef(false);

    // Use refs for callbacks to avoid triggering effect re-runs
    const onChangeRef = React.useRef(onChange);
    const onExpiredRef = React.useRef(onExpired);
    const onErrorRef = React.useRef(onError);
    const onTokenChangeRef = React.useRef(onTokenChange);

    // Keep refs updated
    React.useEffect(() => {
        onChangeRef.current = onChange;
        onExpiredRef.current = onExpired;
        onErrorRef.current = onError;
        onTokenChangeRef.current = onTokenChange;
    }, [onChange, onExpired, onError, onTokenChange]);

    // Render the checkbox when it should be visible
    React.useEffect(() => {
        // Only render if shouldRender is true and instance is ready
        if (!shouldRender || !instance?.render) {
            return;
        }

        // Prevent re-rendering if already rendered
        if (hasRenderedRef.current) {
            return;
        }

        // Create a container for the checkbox
        const checkbox = document.createElement('div');

        // Create stable callback wrappers that use refs
        const callbackWrapper = (token: string) => {
            // Notify the fallback hook of the new token
            if (onTokenChangeRef.current) {
                onTokenChangeRef.current(token);
            }
            // Also call the onChange prop if provided
            if (onChangeRef.current) {
                onChangeRef.current(token);
            }
        };

        const expiredWrapper = () => {
            // Notify the fallback hook that token expired
            if (onTokenChangeRef.current) {
                onTokenChangeRef.current(null);
            }
            if (onExpiredRef.current) {
                onExpiredRef.current();
            }
        };

        const errorWrapper = () => {
            if (onErrorRef.current) {
                onErrorRef.current();
            }
        };

        const params: Parameters = {
            sitekey: v2SiteKey,
            callback: callbackWrapper,
            'expired-callback': expiredWrapper,
            'error-callback': errorWrapper,
            size,
            ...(action && { action }),
        };

        // Render the checkbox and store the widget ID
        widgetIdRef.current = instance.render(checkbox, params);
        hasRenderedRef.current = true;

        // Append to the container ref
        if (checkboxContainerRef.current) {
            checkboxContainerRef.current.appendChild(checkbox);
        }

        return () => {
            // Clean up on unmount or when challenge is no longer required
            removeRecaptchaContainer(id);
            widgetIdRef.current = null;
            hasRenderedRef.current = false;
        };
    }, [shouldRender, instance, v2SiteKey, size, action, id]);

    // Don't render anything if the checkbox shouldn't be visible
    if (!shouldRender) {
        return null;
    }

    return (
        <div className={className} {...props}>
            <div
                ref={checkboxContainerRef}
                id={id}
                tabIndex={-1}
                role="group"
                aria-label="reCAPTCHA verification"
                className={styles.recaptchaCheckbox}
            />
        </div>
    );
}
